import * as Phaser from "phaser";
import {
  AssetLocator,
  type AccessoryType,
  type Animation,
  DEFAULT_ANIMATION,
} from "./AssetLocator";

/**
 * Helper wrapper around one equipped accessory on a Cat.
 *
 * It owns one or two Phaser.Sprite instances (clothes = back + front, hat / glasses = single).
 * The sprites themselves are added straight into the provided `parent` container so they
 * remain part of the Cat display list and can still be depth-sorted relative to every
 * other body part. This small class just keeps the bookkeeping logic (asset lookup,
 * animation switching, flipping, cleanup) out of CatSprite.ts.
 */
export class AccessorySprite {
  private sprites: Phaser.GameObjects.Sprite[] = [];
  private currentAnimation: Animation = DEFAULT_ANIMATION;

  /**
   * @param parent   The Cat container to which the accessory sprites should be added.
   * @param scene    Current Phaser scene.
   * @param locator  AssetLocator already configured for the current cat.
   * @param type     Accessory slot ("clothes" | "hat" | "glasses").
   * @param key      Selected accessory key (e.g. "blue_shirt").
   * @param isFacingLeft Mirror sprites on X when true.
   * @param depthMap   Mapping coming from CatSprite.DEFAULT_DEPTHS merged with overrides.
   */
  constructor(
    parent: Phaser.GameObjects.Container,
    scene: Phaser.Scene,
    private locator: AssetLocator,
    private type: AccessoryType,
    private key: string,
    isFacingLeft: boolean,
    depthMap: { [part: string]: number } = {},
  ) {
    // Create sprite instances for the chosen accessory and insert into parent
    // Visual centering offset to match cat sprites
    const VISUAL_CENTER_OFFSET = 137.5;

    let activeScene: Phaser.Scene | undefined = scene ?? (parent as any).scene;
    // Final fallback: try the globally exposed scene used by React overlay
    if (!activeScene && typeof window !== "undefined") {
      activeScene = (window as any).PhaserSceneReact as Phaser.Scene;
    }

    if (!activeScene) {
      // In extremely rare cases the cat may have been destroyed between the
      // equip() call and the accessory instantiation. Fail gracefully to keep
      // the showcase usable instead of crashing the whole app.
      console.warn(
        "AccessorySprite: activeScene is undefined – skipping accessory creation",
      );
      return;
    }

    if (type === "clothes") {
      // Clothes consist of two layers – back and front
      const { back, front } = locator.getClothesAssets(key, DEFAULT_ANIMATION);

      const backSprite = activeScene.add.sprite(
        0,
        VISUAL_CENTER_OFFSET,
        back.key,
      );
      backSprite.setOrigin(0.5, 0.5);
      backSprite.setDepth(depthMap["clothes_b"] ?? 0);
      backSprite.setFlipX(isFacingLeft);
      parent.add(backSprite);
      this.sprites.push(backSprite);

      const frontSprite = activeScene.add.sprite(
        0,
        VISUAL_CENTER_OFFSET,
        front.key,
      );
      frontSprite.setOrigin(0.5, 0.5);
      frontSprite.setDepth(depthMap["clothes_f"] ?? 0);
      frontSprite.setFlipX(isFacingLeft);
      parent.add(frontSprite);
      this.sprites.push(frontSprite);
    } else {
      // Single-sprite accessory (hat / glasses)
      const asset = locator.getAccessoryAsset(type, key, DEFAULT_ANIMATION);
      const sprite = activeScene.add.sprite(0, VISUAL_CENTER_OFFSET, asset.key);
      sprite.setOrigin(0.5, 0.5);
      const depthName = type; // "hat" | "glasses"
      let depth = depthMap[depthName] ?? 0;
      // Special case: swimming cap should render in front of the body
      if (type === "hat" && this.key === "swimming_cap") {
        depth = depthMap["hat_f"] ?? depth; // use front-hat depth if defined
      }
      sprite.setDepth(depth);
      sprite.setFlipX(isFacingLeft);
      parent.add(sprite);
      this.sprites.push(sprite);
    }
  }

  /**
   * Switch the accessory to the frames for the specified animation.
   */
  play(animation: Animation, loop: boolean, syncProgress?: number) {
    this.currentAnimation = animation;

    // Helper: resolve the sprite-specific key for a given sprite index
    const getKey = (spriteIndex: number): string => {
      if (this.type === "clothes") {
        const { back, front } = this.locator.getClothesAssets(
          this.key,
          animation,
        );
        return spriteIndex === 0 ? back.key : front.key;
      }
      return this.locator.getAccessoryAsset(this.type, this.key, animation).key;
    };

    this.sprites.forEach((s, idx) => {
      const animKey = getKey(idx);
      if (s.scene.anims.exists(animKey)) {
        // Play first so Phaser creates AnimationState, then optionally sync progress
        s.play({ key: animKey, repeat: loop ? -1 : 0 });

        if (syncProgress !== undefined && !Number.isNaN(syncProgress)) {
          // Guard against NaN values which would throw
          try {
            s.anims.setProgress(syncProgress);
          } catch (e) {
            // Fail silently if setProgress not yet available (shouldn't happen)
          }
        }
      }
    });
  }

  /** Mirror all owned sprites on the X axis */
  setFlipX(value: boolean) {
    this.sprites.forEach((s) => s.setFlipX(value));
  }

  /** Clean up – remove sprites from parent and destroy */
  destroy(parent: Phaser.GameObjects.Container) {
    this.sprites.forEach((s) => {
      parent.remove(s);
      s.destroy();
    });
    this.sprites = [];
  }

  /** Accessor key so CatSprite can early-out when already equipped */
  getKey() {
    return this.key;
  }
}
